import { BACKEND_URL } from "../../config";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// Inventory-admin RTK Query service (thread #1570, msg 1a11c82015cd5c7b,
// Levi 2026-10-08). One service, one set of endpoints, a `table` argument
// selecting which Inventory mirror table is being administered — the same shape
// as positionAdminServices, deliberately, because the two pages are served by
// ONE React component (AdminTableContent) and a second data layer with a
// different contract is how a "shared source of truth" turns into two.
//
// WHAT IS DIFFERENT FROM THE POSITIONS SERVICE, AND WHY:
//   * /list is PAGINATED. The Inventory tables run to 20,150 rows
//     (weekly_inventory_fuj7) and 19,276 (line_items_6isj); the positions
//     tables are 25 and 327, which is why that one returns a whole table and
//     this one cannot. `page`, `pageSize` and `q` are server arguments, and the
//     response carries `total` / `page` / `pages` so the page can show where it
//     is. Sorting is server-side too (`sort`, `dir`), because sorting one page
//     of 100 rows and calling it the table's order would be a lie.
//   * listInventoryTables exists — the page is a REGISTRY of ~20 tables, so it
//     needs one call that names them and counts their rows before the user picks
//     one. The Positions page has nothing to switch between.
//
// The BFF gates every call server-side on the "Inventory Admin" TABC row
// (inventoryAdminController.requireInventoryAdmin), which today resolves to the
// President position only, so `table` can only ever name a table the server
// already decided this caller may see.
//
// Reads hit airtable_mirror (no live Airtable — TL Test 2 PASS).
// updateInventoryRow: PATCH one field on one row (MySQL-first + AT write-back).
// createInventoryRow: POST the Add dialog (mint -> mirror insert -> AT create).
//
// TAGS ARE NAMESPACED BY TABLE, same reason as positionAdminServices: row ids
// are unique within a table, and one tag type is shared across ~20 tables, so
// `${table}:${id}` keeps an edit on Items from invalidating a row on Vendors.
// The list tag carries the full query arg (including the page), because
// invalidating page 3 must not be confused with invalidating page 1.
const normTable = (table) => (table ? String(table) : "items");
const tag = (table, id) => `${normTable(table)}:${id}`;

//! One list-tag per (table, page, search) — the arg object is the cache key, so
//! the tag has to be too. A shared `LIST` tag across pages would refresh a page
//! the user is not looking at and leave the one they are on stale.
const listTag = (arg) => {
  const a = arg && typeof arg === "object" ? arg : { table: arg };
  return `${normTable(a.table)}:_list:${a.page || 1}:${a.q || ""}:${a.sort || ""}:${a.dir || ""}`;
};

export const inventoryAdminApi = createApi({
  reducerPath: "inventoryAdminApi",
  baseQuery: fetchBaseQuery({
    baseUrl: BACKEND_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["InventoryRow", "TableRegistry"],
  endpoints: (builder) => ({
    // The page's table of contents: every registered Inventory table with its
    // real row count and field counts, so the switcher can say "Items · 1,204
    // records · 42 fields" before anything is opened.
    listInventoryTables: builder.query({
      query: () => `/inventory-admin/portal/tables`,
      providesTags: [{ type: "InventoryRow", id: "TABLES" }],
    }),
    // M2g12: the "Link to" choices for a linked-record field, on every page that creates fields
    // (Positions and Inventory Admin). The BFF serves the engine tables; `table` picks which page's gate applies.
    listLinkTables: builder.query({
      query: (table) => `/inventory-admin/portal/fields/link-tables?table=${encodeURIComponent(table)}`,
    }),
    // One PAGE of one table, plus that table's full derived field catalog and
    // display config — the catalog comes back on every list call so a field
    // added in Airtable appears on the next load with no client change.
    listInventoryTable: builder.query({
      query: ({ table, page = 1, pageSize = 100, q = "", sort = "", dir = "", filters = [] } = {}) => ({
        url: `/inventory-admin/portal/list`,
        params: {
          table: normTable(table),
          page,
          pageSize,
          ...(q ? { q } : {}),
          ...(sort ? { sort, dir: dir || "asc" } : {}),
          // The filter tree (BFF #3300; serverFilters.js) or the older flat
          // array of {key,op,value}, applied over the whole table in SQL.
          ...((Array.isArray(filters) ? filters.length : filters?.conditions?.length) ? { filters: JSON.stringify(filters) } : {}),
        },
      }),
      providesTags: (result, error, arg) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({
                type: "InventoryRow",
                id: tag(arg?.table, id),
              })),
              { type: "InventoryRow", id: listTag(arg) },
            ]
          : [{ type: "InventoryRow", id: listTag(arg) }],
    }),
    getInventoryRow: builder.query({
      query: ({ recid, table }) => ({
        url: `/inventory-admin/portal/get/${recid}`,
        params: { table: normTable(table) },
      }),
      providesTags: (result, error, { recid, table }) => [
        { type: "InventoryRow", id: tag(table, recid) },
      ],
    }),
    // Linked-record picker. `field` is the CATALOG key of the link field, not a
    // table key — the server resolves the target table from that field's own
    // linkedTableId, so a link field added in Airtable becomes pickable with no
    // change here. Same request/response shape as the position endpoint so
    // LinkRecordPicker can consume either.
    getInventoryLinkOptions: builder.query({
      query: ({ table, field, q = "", ids = [] }) => ({
        url: `/inventory-admin/portal/link-options`,
        params: {
          table: normTable(table),
          field,
          ...(q ? { q } : {}),
          ...(ids.length ? { ids: ids.join(",") } : {}),
        },
      }),
      keepUnusedDataFor: 30,
    }),
    // Cell editor — PATCH one field on one row.
    // Arg: { recid, field, value, table }
    updateInventoryRow: builder.mutation({
      query: ({ recid, field, value, table }) => ({
        url: `/inventory-admin/portal/update/${recid}`,
        params: { table: normTable(table) },
        method: "PATCH",
        body: { field, value },
      }),
      // Optimistic update — apply immediately, roll back if the BFF errors. The
      // rollback matters more here than on the Positions page: a currency or
      // quantity cell that silently keeps a rejected value is inventory data a
      // manager would act on.
      async onQueryStarted(
        { recid, field, value, table },
        { dispatch, queryFulfilled, getState }
      ) {
        const patches = [];
        for (const { endpointName, originalArgs } of Object.values(
          inventoryAdminApi.util.selectInvalidatedBy(getState(), [
            { type: "InventoryRow", id: tag(table, recid) },
          ])
        )) {
          if (endpointName === "listInventoryTable" && originalArgs) {
            patches.push(
              dispatch(
                inventoryAdminApi.util.updateQueryData(
                  "listInventoryTable",
                  originalArgs,
                  (draft) => {
                    const row = draft.data?.find((r) => r.id === recid);
                    if (row) row.fields[field] = value;
                  }
                )
              )
            );
          }
          if (endpointName === "getInventoryRow") {
            patches.push(
              dispatch(
                inventoryAdminApi.util.updateQueryData(
                  "getInventoryRow",
                  originalArgs,
                  (draft) => {
                    if (draft.data) draft.data.fields[field] = value;
                  }
                )
              )
            );
          }
        }
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
      // Invalidate the row AND every page of that table's list: the row may have
      // moved under the current sort (a renamed title, a changed currency), so
      // refreshing only the page it was on can leave it in the wrong place.
      invalidatesTags: (result, error, { recid, table }) => [
        { type: "InventoryRow", id: tag(table, recid) },
        { type: "InventoryRow", id: "TABLES" },
      ],
    }),
    // Add dialog — POST every walked field at once.
    // Arg: { values: { <catalog key>: value }, table }
    createInventoryRow: builder.mutation({
      query: ({ values, table }) => ({
        url: `/inventory-admin/portal/create`,
        params: { table: normTable(table) },
        method: "POST",
        body: { values },
      }),
      invalidatesTags: (result, error, { table }) => [
        { type: "InventoryRow", id: "TABLES" },
        { type: "InventoryRow", id: `${normTable(table)}:_list:1:::` },
      ],
    }),
    // Detail modal — DELETE one row.
    // Arg: { recid, table }
    //
    // ONLY the two tables that had a delete before the move (Vendors and
    // Equipment) accept this; the BFF answers 403 for every other table, so the
    // control is rendered from the server's own `config.deletable` rather than
    // from a client-side list of table keys — the client cannot drift from what
    // the server will actually allow.
    //
    // No optimistic removal, deliberately. The row is gone from the list only
    // after the server confirms it: a delete the server rejected would otherwise
    // blink out of the table and come back, which reads as a bug rather than as
    // a refusal. Same reason the invalidation covers the row tag as well as the
    // table's list.
    deleteInventoryRow: builder.mutation({
      query: ({ recid, table }) => ({
        url: `/inventory-admin/portal/delete/${recid}`,
        params: { table: normTable(table) },
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { recid, table }) => [
        { type: "InventoryRow", id: tag(table, recid) },
        { type: "InventoryRow", id: "TABLES" },
      ],
    }),
    // Portal table registry (portal-airtable-parity T29, UI half). Rows live in
    // portal_app.table_registry (V168) and the BFF routes are T29b. President only:
    // the BFF answers 403 for anyone else, and the panel that uses these hides
    // itself on 403. MySQL only; nothing here reads or writes Airtable.
    listTableRegistry: builder.query({
      query: () => `/table-registry/portal`,
      providesTags: [{ type: "TableRegistry", id: "LIST" }],
    }),
    // Arg: { key, displayName }. The BFF validates the key and answers 409 on a duplicate.
    createTableRegistry: builder.mutation({
      query: ({ key, displayName }) => ({
        url: `/table-registry/portal`,
        method: "POST",
        body: { key, displayName },
      }),
      invalidatesTags: [{ type: "TableRegistry", id: "LIST" }],
    }),
  }),
});

export const {
  useListTableRegistryQuery,
  useCreateTableRegistryMutation,
  useListInventoryTablesQuery,
  useListInventoryTableQuery,
  useListLinkTablesQuery,
  useGetInventoryRowQuery,
  useLazyGetInventoryLinkOptionsQuery,
  useUpdateInventoryRowMutation,
  useCreateInventoryRowMutation,
  useDeleteInventoryRowMutation,
} = inventoryAdminApi;
